import { ApiError } from './api'

export interface PaginatedDataState<T> {
  data: T[]
  hasNextPage?: boolean
  nextPageOffset?: number
}

export const PaginatedDataState = {
  empty: <T>() => {
    return {
      data: [] as T[],
      hasNextPage: false,
      nextPageOffset: undefined,
    }
  },
  from: <T>(data: T[], hasNextPage?: boolean, nextPageOffset?: number) => {
    return {
      data,
      hasNextPage,
      nextPageOffset,
    } as PaginatedDataState<T>
  },
} as const

export enum ResourceLoadingState {
  Idle,
  Loading,
  LoadSuccess,
  LoadError,
}

export enum ResourceLoadingType {
  NoResult,
  SingleResult,
  StartOfPage,
  NextPage,
}

export enum ResourceLoadingErrorType {
  NotFound = 'notFound',
  Unauthorized = 'unauthorized',
  Unknown = 'unknown',
}

export interface ResourceLoadingDisposition {
  state: ResourceLoadingState
  loadingType?: ResourceLoadingType
  errorType?: ResourceLoadingErrorType
}

const getErrorTypeFromApiError = (apiError?: ApiError) => {
  if (!apiError) return ResourceLoadingErrorType.Unknown

  if (apiError.status === 404) return ResourceLoadingErrorType.NotFound
  if (apiError.status === 403) return ResourceLoadingErrorType.Unauthorized

  return ResourceLoadingErrorType.Unknown
}

export const ResourceLoadingDisposition = {
  Idle: {
    state: ResourceLoadingState.Idle,
  } as const,
  Loading: {
    state: ResourceLoadingState.Loading,
  } as const,
  LoadSuccess: {
    state: ResourceLoadingState.LoadSuccess,
  } as const,
  LoadingWithType: (loadingType: ResourceLoadingType) => {
    return {
      state: ResourceLoadingState.Loading,
      loadingType,
    }
  },
  LoadSuccessWithType: (loadingType: ResourceLoadingType) => {
    return {
      state: ResourceLoadingState.LoadSuccess,
      loadingType,
    }
  },
  LoadError: (errorType: ResourceLoadingErrorType) =>
    ({
      state: ResourceLoadingState.LoadError,
      errorType,
    }) as const,
  FromApiError: (apiError?: ApiError) => {
    return {
      state: ResourceLoadingState.LoadError,
      errorType: getErrorTypeFromApiError(apiError),
    }
  },
} as const
Object.freeze(ResourceLoadingDisposition)
